import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FlagIcon } from '../ui/FlagIcon';
import { countryMeta } from '../utils/country-meta';
import { supportsFlagEmoji } from '../utils/flag-support';
import type { RouteState } from '../wizard/types';

/**
 * Row used to drive the country pickers. Combines a backend country code with
 * the locally-resolved flag glyph + label so the picker doesn't need to know
 * anything about the catalog API surface.
 */
export interface CountryOption {
  code: string;
  name: string;
  flag: string;
  /** ISO 4217 currency for the country. Drives the amount field's input mode. */
  currency: string;
}

interface RouteAmountStepProps {
  origins: CountryOption[];
  destinations: CountryOption[];
  route: RouteState;
  onChange: (patch: Partial<RouteState>) => void;
  /**
   * Called when the user explicitly confirms the route — typically by pressing
   * the keyboard's Enter / Done / Go key (mobile "OK" tick). The parent should
   * advance the wizard to the next step only when the route is complete.
   */
  onAdvance: () => void;
}

const CountrySelect = ({
  value,
  options,
  label,
  onChange,
}: {
  value: string | null;
  options: CountryOption[];
  label: string;
  onChange: (code: string) => void;
}) => {
  // On Windows the flag emoji degrades to the stylized letters "AR"/"US",
  // which is redundant next to the country name we already show — so we only
  // render the flag on platforms that draw real flag glyphs (iOS/Android/mac).
  const [flagsRenderAsFlags] = useState(supportsFlagEmoji);
  const selected = options.find((c) => c.code === value) ?? options[0];

  if (!selected) {
    // Empty catalog (still loading or partner with zero supported routes).
    // Render a placeholder pill so the layout doesn't collapse.
    return (
      <div className="border-border bg-bg flex w-full items-center gap-3 rounded-xl border px-3 py-3">
        <span className="text-fg-muted text-[10px] font-semibold uppercase tracking-wide">
          {label}
        </span>
      </div>
    );
  }

  return (
    <label className="border-border bg-bg flex w-full items-center gap-2 rounded-xl border px-3 py-3">
      {flagsRenderAsFlags && (
        <FlagIcon code={selected.code} className="shrink-0 text-xl" />
      )}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-fg-muted text-[10px] font-semibold uppercase tracking-wide">
          {label}
        </span>
        <select
          value={selected.code}
          onChange={(e) => onChange(e.target.value)}
          className="text-fg w-full max-w-full appearance-none truncate bg-transparent text-sm font-semibold focus:outline-none"
        >
          {options.map((c) => (
            <option key={c.code} value={c.code} className="bg-bg-elevated">
              {c.name}
            </option>
          ))}
        </select>
      </span>
      <span className="text-fg-muted shrink-0 text-xs" aria-hidden>
        ▾
      </span>
    </label>
  );
};

export const RouteAmountStep = ({
  origins,
  destinations,
  route,
  onChange,
  onAdvance,
}: RouteAmountStepProps) => {
  const { t, i18n } = useTranslation('smart');

  // The amount is always entered in the DESTINATION currency — the recipient
  // receives exactly what's typed; the sender's USD equivalent is derived by
  // the quote engine. This matches how senders think ("I want them to get
  // $50,000 MXN") and avoids the rounding ambiguity of source-side amounts.
  const destinationCountry =
    destinations.find((c) => c.code === route.toCountry) ??
    destinations[0] ?? {
      code: '??',
      name: '—',
      flag: 'xx',
      currency: countryMeta(route.toCountry).defaultCurrency,
    };

  // Decimal separator for the active locale (',' for es/fr, '.' for en). The
  // canonical amount in wizard state always uses '.' (it's consumed via
  // parseFloat downstream); this is only how we RENDER it back to the user.
  const decimalSeparator = useMemo(
    () =>
      new Intl.NumberFormat(i18n.language)
        .formatToParts(1.1)
        .find((part) => part.type === 'decimal')?.value ?? '.',
    [i18n.language],
  );

  // Sanitize free-form input. We use type="text" (not type="number") because
  // iOS Safari's `type="number"` keypad has no return/Done key — `type="text"`
  // + `inputMode="decimal"` shows a numeric pad that includes a confirm key
  // inside a form.
  //
  // Both '.' and ',' are accepted as the decimal separator regardless of the
  // active locale — users type whatever their keyboard or habit dictates. The
  // LAST separator typed wins as the decimal mark (so "1.234,56" → 1234.56),
  // and the fraction is truncated to 2 digits. The stored value is normalized
  // to a dot so parseFloat keeps working everywhere else.
  const handleAmountChange = (raw: string) => {
    const cleaned = raw.replace(/[^0-9.,]/g, '');
    const lastSep = Math.max(cleaned.lastIndexOf('.'), cleaned.lastIndexOf(','));
    if (lastSep === -1) {
      onChange({ amount: cleaned });
      return;
    }
    const intPart = cleaned.slice(0, lastSep).replace(/[.,]/g, '');
    const fracPart = cleaned.slice(lastSep + 1).slice(0, 2);
    onChange({ amount: `${intPart}.${fracPart}` });
  };

  // Render the canonical dot-decimal with the locale's separator so an es/fr
  // user sees "1500,5" even though state holds "1500.5".
  const displayAmount = (route.amount ?? '').replace('.', decimalSeparator);

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        onAdvance();
      }}
    >
      <div className="grid grid-cols-2 items-center gap-2">
        <CountrySelect
          value={route.fromCountry}
          options={origins}
          label={t('wizard.compose.routeStep.from')}
          onChange={(code) => onChange({ fromCountry: code })}
        />
        <CountrySelect
          value={route.toCountry}
          options={destinations}
          label={t('wizard.compose.routeStep.to')}
          onChange={(code) => onChange({ toCountry: code })}
        />
      </div>

      <label className="border-border bg-bg flex w-full items-center gap-3 rounded-xl border px-4 py-3">
        <span className="text-fg-muted text-xl font-semibold">
          {destinationCountry.currency === 'USD' ? '$' : destinationCountry.currency}
        </span>
        <input
          type="text"
          inputMode="decimal"
          enterKeyHint="done"
          autoComplete="off"
          placeholder="0"
          value={displayAmount}
          onChange={(e) => handleAmountChange(e.target.value)}
          onBlur={(e) => {
            // iOS Safari numeric keypads expose no Enter key — the only way
            // the user signals "I'm done" is the "Done" button in the keyboard
            // accessory bar, which simply dismisses the keyboard and fires
            // blur (no form submit). We treat blur as the mobile confirmation
            // signal, but skip it when focus is moving to another control
            // inside the same form (e.g. the country selector) so the user
            // can still tweak the route without auto-advancing.
            const next = e.relatedTarget as HTMLElement | null;
            if (next && e.currentTarget.form?.contains(next)) return;
            onAdvance();
          }}
          className="text-fg w-full appearance-none bg-transparent text-2xl font-semibold tabular-nums focus:outline-none"
        />
        <span className="text-fg-muted text-xs uppercase tracking-wide">
          {destinationCountry.currency}
        </span>
      </label>
      <p className="text-fg-muted text-[11px] leading-snug">
        {t('wizard.compose.routeStep.amountHint', {
          currency: destinationCountry.currency,
        })}
      </p>

      {/* Hidden submit so iOS reliably treats the form as submittable from the
          keyboard's confirm key, even when no visible button is present. */}
      <button type="submit" className="sr-only" tabIndex={-1} aria-hidden>
        {t('wizard.compose.routeStep.submit')}
      </button>
    </form>
  );
};
